// Agent DTO + tool definition. An Agent is a reusable configuration: prompt,
// model policy, tool allowlist, budget ceilings. Dashboard creates/edits;
// runtime reads. The tenant binding is enforced server-side via RLS — never
// trust a client-supplied tenantId.
import { Type } from '@sinclair/typebox';
import { ProviderSchema } from './providers.js';
// ---------- Tool definitions ----------
// Tools are declared by name + JSON Schema of arguments. The platform runs
// them — agents never invoke code directly. Allowlisted tool names only.
export const ToolParamSchemaSchema = Type.Object({
    type: Type.Literal('object'),
    // Per-property JSON Schema is stored as opaque JSON. Full validation
    // happens at tool-invocation time against the server-side allowlist.
    properties: Type.Record(Type.String(), Type.Unknown()),
    required: Type.Optional(Type.Array(Type.String(), { maxItems: 32 })),
    additionalProperties: Type.Optional(Type.Boolean()),
}, { additionalProperties: false });
export const ToolDefinitionSchema = Type.Object({
    name: Type.String({ minLength: 1, maxLength: 64, pattern: '^[a-z][a-z0-9_]*$' }),
    description: Type.String({ minLength: 1, maxLength: 500 }),
    parameters: ToolParamSchemaSchema,
}, { $id: 'ToolDefinition', additionalProperties: false });
// ---------- Agent policy ----------
export const AgentBudgetSchema = Type.Object({
    // Hard ceiling per run — exceeding it aborts with `aborted_by_ceiling`.
    maxCostUsd: Type.Number({ minimum: 0, maximum: 100 }),
    maxSteps: Type.Integer({ minimum: 1, maximum: 32 }),
    wallClockMs: Type.Integer({ minimum: 1_000, maximum: 120_000 }),
}, { additionalProperties: false });
export const AgentModelPolicySchema = Type.Object({
    provider: ProviderSchema,
    model: Type.String({ minLength: 1, maxLength: 128 }),
    temperature: Type.Optional(Type.Number({ minimum: 0, maximum: 2 })),
    maxOutputTokens: Type.Optional(Type.Integer({ minimum: 1, maximum: 16_384 })),
}, { additionalProperties: false });
// Opt-in 5xx/timeout recovery. Both fields required together — the CRUD
// route rejects a partial pair, matching the DB CHECK constraint.
export const AgentFallbackSchema = Type.Object({
    provider: ProviderSchema,
    model: Type.String({ minLength: 1, maxLength: 128 }),
}, { additionalProperties: false });
// ---------- DTOs ----------
export const AgentSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    tenantId: Type.String({ format: 'uuid' }),
    name: Type.String({ minLength: 1, maxLength: 128 }),
    systemPrompt: Type.String({ minLength: 1, maxLength: 10_000 }),
    modelPolicy: AgentModelPolicySchema,
    // Null when the agent has no fallback configured. Explicit null (vs.
    // omitting the field) keeps the wire contract obvious.
    fallback: Type.Union([AgentFallbackSchema, Type.Null()]),
    knowledgeBaseId: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    budget: AgentBudgetSchema,
    tools: Type.Array(ToolDefinitionSchema, { maxItems: 16 }),
    // Allowlisted outbound domains for `http_fetch`-style tools. SSRF guard
    // still applies regardless.
    allowedDomains: Type.Array(Type.String({ maxLength: 253 }), { maxItems: 32 }),
    // Browser origins the widget may be embedded from. Each entry is compared
    // exactly (scheme + host + port) against the caller's Origin header on
    // /v1/widget/session. Empty = widget hosting disabled for this agent.
    allowedOrigins: Type.Array(Type.String({ maxLength: 253 }), { maxItems: 32 }),
    // True for the onboarding-tour demo ("Cortex Demo") created via
    // POST /v1/agents/sample. Sample agents do not count against
    // plan_limits.included_agents — see migration 0018. Set at creation
    // time only; the update path doesn't expose this field.
    isSample: Type.Boolean(),
    // True when the agent's persisted `model` is no longer in the tenant's
    // plan_limits.allowed_models — typically after a downgrade. Computed
    // server-side at list/read time (not stored on the row); the chat-time
    // model gate rejects requests for these agents with `model_not_in_plan`,
    // and the dashboard surfaces the per-row badge to drive the user to fix it.
    // Defaults to false on tenants without a plan (pre-billing) so the
    // dashboard renders neutrally.
    planMismatch: Type.Boolean({ default: false }),
    // ISO timestamp when reconcileTenantToPlan soft-disabled the agent
    // (migration 0025). Null = active. Disabled agents are returned by
    // GET /v1/agents so the dashboard can render their row state and
    // expose the Enable CTA, but the chat path rejects them at resolve
    // time with `agent_disabled` (403).
    disabledAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    // 'plan_downgrade' | 'cancel_to_free' | 'manual' — the reason the
    // disable happened. Mirrors the DB CHECK constraint values. Null
    // when disabledAt is null. Surfaced so the dashboard can render
    // tier-specific copy ("disabled by your recent downgrade") in
    // the per-row tooltip.
    disabledReason: Type.Union([Type.String(), Type.Null()]),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
}, { $id: 'Agent', additionalProperties: false });
// Create/update inputs: client never sets id/tenantId/timestamps.
export const CreateAgentInputSchema = Type.Object({
    name: Type.String({ minLength: 1, maxLength: 128 }),
    systemPrompt: Type.String({ minLength: 1, maxLength: 10_000 }),
    modelPolicy: AgentModelPolicySchema,
    fallback: Type.Optional(AgentFallbackSchema),
    knowledgeBaseId: Type.Optional(Type.String({ format: 'uuid' })),
    budget: AgentBudgetSchema,
    tools: Type.Array(ToolDefinitionSchema, { maxItems: 16 }),
    allowedDomains: Type.Array(Type.String({ maxLength: 253 }), { maxItems: 32 }),
    // Optional — omit for agents that don't need widget hosting. The DB
    // default is the empty array.
    allowedOrigins: Type.Optional(Type.Array(Type.String({ maxLength: 253 }), { maxItems: 32 })),
}, { $id: 'CreateAgentInput', additionalProperties: false });
// Update: every field optional. `fallback: null` is how a client removes a
// previously configured fallback (vs. "don't touch it").
export const UpdateAgentInputSchema = Type.Object({
    name: Type.Optional(Type.String({ minLength: 1, maxLength: 128 })),
    systemPrompt: Type.Optional(Type.String({ minLength: 1, maxLength: 10_000 })),
    modelPolicy: Type.Optional(AgentModelPolicySchema),
    fallback: Type.Optional(Type.Union([AgentFallbackSchema, Type.Null()])),
    knowledgeBaseId: Type.Optional(Type.Union([Type.String({ format: 'uuid' }), Type.Null()])),
    budget: Type.Optional(AgentBudgetSchema),
    tools: Type.Optional(Type.Array(ToolDefinitionSchema, { maxItems: 16 })),
    allowedDomains: Type.Optional(Type.Array(Type.String({ maxLength: 253 }), { maxItems: 32 })),
    allowedOrigins: Type.Optional(Type.Array(Type.String({ maxLength: 253 }), { maxItems: 32 })),
}, { $id: 'UpdateAgentInput', additionalProperties: false });
// POST /v1/agents/sample — one-click "Cortex Demo" agent for non-tech users.
// No request body — the endpoint hard-codes safe defaults and pre-populates
// a small FAQ knowledge base so the playground works without any setup.
// Idempotent: if the tenant already has the demo agent, returns its ids.
export const CreateSampleAgentResultSchema = Type.Object({
    agentId: Type.String({ format: 'uuid' }),
    knowledgeBaseId: Type.String({ format: 'uuid' }),
    // True when the call returned an existing demo agent unchanged.
    // Lets the dashboard skip the "preparing your assistant…" loading state
    // on subsequent visits.
    alreadyExists: Type.Boolean(),
}, { $id: 'CreateSampleAgentResult', additionalProperties: false });
// POST /v1/agents/:id/playground-session — mints a widget-style session for
// the in-dashboard playground. Bypasses the Origin allowlist that the public
// /v1/widget/session enforces because the caller is the authed dashboard
// user, not a public widget consumer. Same response shape as widget/session
// so the dashboard can hand it to the same widget runtime.
export const MintPlaygroundSessionResultSchema = Type.Object({
    sessionToken: Type.String({ minLength: 32, maxLength: 128 }),
    expiresAt: Type.String({ format: 'date-time' }),
    messageCap: Type.Integer({ minimum: 1 }),
}, { $id: 'MintPlaygroundSessionResult', additionalProperties: false });
//# sourceMappingURL=agent.js.map